import React from 'react';
import { Link } from 'react-router';

import Title from './title.jsx';
import NotesControlPanel from './all_notes/control_panel.jsx';
import Chapter from './all_notes/chapter.jsx';
import OfflineMenu from './offline_menu.jsx';
import Navbar from './navbar.jsx';

import PropTypes from 'prop-types';

import R from 'ramda';

const setOf = R.uniq;

const MESSAGES = {
    HEADER: {
        NO_NOTES: 'No results',
        NO_RESULTS: 'No results',
        NO_QUESTIONS: 'No questions available',
        NO_UNANSWERED: 'All questions have been answered',
    },
    BODY: {
        NO_NOTES: 'There are no notes available for this material.',
        NO_RESULTS:
            'Try adjusting your filters to see notes for this material.',
        NO_QUESTIONS: 'Looks like there are no questions to be answered.',
        NO_UNANSWERED:
            "Nice work! Looks like you've answered all questions. Try adjusting your filters to see notes for this material.",
    },
};

export default class AllNotes extends React.Component {
    static displayName = 'AllNotes';

    static propTypes = {
        toggleControlPanelVisibility: PropTypes.func.isRequired,
        controlPanelIsOpen: PropTypes.bool.isRequired,
        routeToContentPage: PropTypes.func.isRequired,
        routeToAllNotes: PropTypes.func.isRequired,
        children: PropTypes.object,
        analyticsEvent: PropTypes.func,
        allChapters: PropTypes.any,
        closeControlPanel: PropTypes.func.isRequired,
        allClassmates: PropTypes.any,
        allInstructors: PropTypes.any,
        allNotes: PropTypes.any,
        allPages: PropTypes.any,
        allTags: PropTypes.any,
        content: PropTypes.any,
        contentId: PropTypes.number.isRequired,
        current_user_uri: PropTypes.string,
        isInstructor: PropTypes.bool.isRequired,
        query: PropTypes.object.isRequired,
        unansweredList: PropTypes.any,
        hideSharingQA: PropTypes.bool.isRequired,
        origin: PropTypes.string.isRequired,
        setOrigin: PropTypes.func.isRequired,
        addAural: PropTypes.func.isRequired,
    };

    getQueryParameters() {
        const {
            page = -1,
            selectedTags = [],
            excludeClassmates = [],
            unansweredQuestions,
        } = this.props.query;

        // You can't select unanswered questions without also selecting questions
        let { questions } = this.props.query;
        if (unansweredQuestions === 'true') {
            questions = 'true';
        }

        return {
            page: parseInt(page, 10),
            selectedTags: setOf(selectedTags),
            excludeClassmates: setOf(excludeClassmates),
            questions: questions === 'true',
            unansweredQuestions: unansweredQuestions === 'true',
        };
    }

    setPage(pagenum) {
        let page = parseInt(pagenum, 10);
        if (page === -1) {
            // Don't include the parameter when it's the default value
            page = undefined;
        }
        this.updateQuery({ page });
    }

    addTag = tag => {
        const { addAural } = this.props;
        let { selectedTags, page, ...params } = this.getQueryParameters();
        this.updateQuery({
            ...params,
            selectedTags: [...selectedTags, tag],
        });
        addAural(`filtering by ${tag}`);
    };

    generateFilterFunction() {
        const { unansweredList } = this.props;
        const {
            questions,
            unansweredQuestions,
            excludeClassmates,
            selectedTags,
            page,
        } = this.getQueryParameters();

        return function(note) {
            if (questions && !note.is_question) {
                return false;
            }

            if (unansweredQuestions && !R.contains(note, unansweredList)) {
                return false;
            }

            if (page >= 0 && note.page !== page) {
                return false;
            }

            if (selectedTags.length > 0) {
                if (
                    note.answer &&
                    R.difference(selectedTags, note.answer.tags).length <= 0
                ) {
                    return true;
                }
                if (R.difference(selectedTags, note.tags).length > 0) {
                    return false;
                }
            }
            return !R.contains(note.owner, excludeClassmates);
        };
    }

    includeClassmate(uris, includeClassmate) {
        if (typeof uris === 'string') {
            uris = [uris];
        }

        let { excludeClassmates } = this.getQueryParameters();

        if (includeClassmate) {
            excludeClassmates = R.difference(excludeClassmates, uris);
        } else {
            excludeClassmates = R.union(uris, excludeClassmates);
        }
        this.props.routeToAllNotes({
            contentId: this.props.contentId,
            query: { excludeClassmates },
            routeType: 'replace',
        });
    }

    mergeNotesIntoChapters(notes, chapters) {
        const notesByChapter = R.groupBy(note => {
            const page = note.page;
            return chapters.reduce((acc, chapter, index) => {
                return chapter.first_page <= page && page <= chapter.last_page
                    ? index
                    : acc;
            }, 0);
        }, notes);
        return chapters.map((chapter, index) => {
            return {
                ...chapter,
                notes: R.propOr([], index, notesByChapter),
            };
        });
    }

    removeTag = tag => {
        const { addAural } = this.props;
        let { selectedTags, page, ...params } = this.getQueryParameters();
        selectedTags = R.difference(selectedTags, [tag]);
        this.updateQuery({ ...params, selectedTags });
        addAural(`Removing filter ${tag}`);
    };

    toggleControlPanel(e) {
        e.preventDefault();
        this.props.toggleControlPanelVisibility();
    }

    toggleQuestions = () => {
        const { page, ...params } = this.getQueryParameters();
        let { unansweredQuestions, questions } = params;
        questions = !questions;
        questions = questions ? true : undefined;

        if (!questions) {
            unansweredQuestions = questions;
        }
        this.updateQuery({ ...params, questions, unansweredQuestions });
    };

    toggleUnansweredQuestions = () => {
        let {
            unansweredQuestions,
            page,
            ...params
        } = this.getQueryParameters();
        unansweredQuestions = !unansweredQuestions;
        this.updateQuery({
            ...params,
            unansweredQuestions: unansweredQuestions ? true : undefined,
        });
    };

    updateQuery(query) {
        this.props.routeToAllNotes({
            contentId: this.props.contentId,
            query,
            routeType: 'replace',
        });
    }

    renderNoNotesMessage() {
        const {
            excludeClassmates,
            questions,
            unansweredQuestions,
        } = this.getQueryParameters();
        let situation = 'NO_NOTES';
        if (excludeClassmates.length) {
            situation = 'NO_RESULTS';
        }
        if (questions && !unansweredQuestions) {
            situation = 'NO_QUESTIONS';
        }
        if (unansweredQuestions) {
            situation = 'NO_UNANSWERED';
        }
        return (
            <div role="contentinfo" className="not-found">
                <h2>{MESSAGES.HEADER[situation]}</h2>
                <p>{MESSAGES.BODY[situation]}</p>
            </div>
        );
    }

    resetOrigin = () => {
        this.props.setOrigin('');
    };

    renderChapters(chapters) {
        const chapterNotes = chapters
            .filter(({ notes: { length } }) => length)
            .map((chapter, index) => {
                return (
                    <Chapter
                        key={index}
                        chapter={chapter}
                        allPages={this.props.allPages}
                        addTag={this.addTag}
                    />
                );
            });
        return chapterNotes.length ? chapterNotes : this.renderNoNotesMessage();
    }

    render() {
        const {
            allClassmates,
            allNotes,
            allChapters,
            content,
            children,
            hideSharingQA,
            origin,
        } = this.props;
        const {
            questions,
            unansweredQuestions,
            excludeClassmates,
            page,
            selectedTags,
        } = this.getQueryParameters();
        if (!allChapters) {
            return false;
        }

        // This is very bad for performance. We shouldn't create objects inside
        // render.
        const buildSelectedClassmates = R.compose(
            R.filter(R.complement(R.contains(R.__, excludeClassmates))),
            R.keys
        );

        const showSelectedClassmates = buildSelectedClassmates(allClassmates);

        const notes = allNotes.filter(this.generateFilterFunction());
        const chapters = this.mergeNotesIntoChapters(notes, allChapters);
        const noteCount = chapters.reduce((total, chapter) => {
            return total + chapter.notes.length;
        }, 0);
        const toggleControlPanel = this.toggleControlPanel.bind(this);
        const headerText = noteCount === 1 ? ' Note' : ' Notes';

        const childComponent = children
            ? React.cloneElement(children, { content })
            : false;
        const linkToClose = origin || `content-${content.id}`;
        const linkToNewNote = `notes/content-${content.id}/new`;
        return (
            <div className="content-panel all-notes clearfix">
                <span className="aural" role="status">
                    {noteCount} {headerText} displayed for {content.title}
                </span>
                <Navbar.Proxy>
                    <h1>{content.title}</h1>
                    <div className="pull-right header-right">
                        <OfflineMenu toolTipDirection="left" />
                        <Link
                            to={linkToClose}
                            onClick={this.resetOrigin}
                            className="icon cl-icon cl-icon-close-thin"
                        >
                            <span className="aural">Close notes view</span>
                        </Link>
                    </div>
                </Navbar.Proxy>
                <Title>All notes for {content.title}</Title>

                {childComponent}
                <NotesControlPanel
                    isOpen={this.props.controlPanelIsOpen}
                    toggleVisibility={toggleControlPanel}
                    closeControlPanel={this.props.closeControlPanel}
                    addTag={this.addTag}
                    setPage={this.setPage.bind(this)}
                    removeTag={this.removeTag}
                    allClassmates={this.props.allClassmates}
                    allInstructors={this.props.allInstructors}
                    allPages={this.props.allPages}
                    onlyPage={page}
                    showSelectedClassmates={showSelectedClassmates}
                    includeClassmate={this.includeClassmate.bind(this)}
                    currentUserURI={this.props.current_user_uri}
                    isInstructor={this.props.isInstructor}
                    analyticsEvent={this.props.analyticsEvent}
                    selectedTags={selectedTags}
                    allTags={this.props.allTags}
                    questions={questions}
                    unansweredQuestions={unansweredQuestions}
                    toggleUnansweredQuestions={this.toggleUnansweredQuestions}
                    toggleQuestions={this.toggleQuestions}
                    slug={this.props.content.slug}
                    hideSharingQA={hideSharingQA}
                    addAural={this.props.addAural}
                />
                <div className="header">
                    <button
                        className="cl-icon cl-icon-filter"
                        onClick={toggleControlPanel}
                    >
                        <span className="aural">Show filter panel</span>
                    </button>
                    <h2 className="js-notes-header">
                        {noteCount} {headerText}
                    </h2>
                    <Link
                        to={linkToNewNote}
                        className="btn-add-note btn-primary js-btn-notes-add"
                        role="button"
                    >
                        <span>
                            <i
                                className="cl-icon cl-icon-plus-light"
                                aria-hidden="true"
                            />
                            Add Note
                        </span>
                    </Link>
                </div>
                <div className="notes" aria-label="Note Listing">
                    {this.renderChapters(chapters)}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/all_notes.ui.jsx